// src/components/VideoThumbnail.tsx
'use client';

import { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import Image from 'next/image';
import VideoItem from './VideoItem';
import { VideoItemProps } from '@/types';
import { useData } from '@/i18n/LangContext';

export default function VideoThumbnail({ videoId, title, index = 0 }: VideoItemProps) {
  const { ui } = useData();
  const [showVideo, setShowVideo] = useState(false);
  const [shouldLoad, setShouldLoad] = useState(index < 30);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const thumbnailRef = useRef<HTMLDivElement>(null);

  // Pfad zum Thumbnail
  const thumbnailPath = `/assets/videos/${videoId}/${videoId}_thumbnail.webp`;

  // Scroll-Debounce mit erweitertem rootMargin je nach Index
  useEffect(() => {
    if (index < 30) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      {
        // Großer rootMargin für obere Elemente, kleiner für weiter unten
        rootMargin: `${Math.max(400 - index, 200)}px`
      }
    );

    if (thumbnailRef.current) {
      observer.observe(thumbnailRef.current);
    }

    return () => observer.disconnect();
  }, [index]);

  // Bild-Loading-Handler
  useEffect(() => {
    if (!shouldLoad) return;

    const img = new window.Image();
    img.src = thumbnailPath;

    img.onload = () => {
      setIsLoaded(true);
      setHasError(false);
    };

    img.onerror = () => {
      setIsLoaded(false);
      setHasError(true);
    };

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [shouldLoad, thumbnailPath]);

  if (showVideo) {
    return <VideoItem videoId={videoId} title={title} />;
  }

  return (
    <div className="relative" ref={thumbnailRef}>
      <motion.div
        data-cy="video-thumbnail"
        className="rounded-lg bg-gray-900 border border-gray-800 shadow-xl overflow-hidden cursor-pointer"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        onClick={() => setShowVideo(true)}
      >
        <div className="relative aspect-video">
          {!shouldLoad || !isLoaded ? (
            <div className="w-full h-full flex items-center justify-center bg-gray-800">
              {shouldLoad ? (
                <div className="size-10 border-2 border-[#ff88ff] border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <div className="text-gray-500 text-xs">{ui.media.deferredLoading}</div>
              )}
            </div>
          ) : hasError ? (
            <div className="w-full h-full flex items-center justify-center bg-gray-800">
              <div className="text-gray-500 text-sm">{ui.media.previewNotAvailable}</div>
            </div>
          ) : (
            <Image
              src={thumbnailPath}
              alt={title}
              fill
              className="object-cover"
            />
          )}

          {/* Overlay mit Play-Button */}
          <div className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors">
            <div className="bg-[#ff88ff]/80 rounded-full p-4 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white">
                <polygon points="5 3 19 12 5 21 5 3"></polygon>
              </svg>
            </div>
          </div>

          {/* Videotitel */}
          <div className="absolute top-0 left-0 right-0 bg-black/60 p-2 text-white z-10">
            {title}
          </div>
        </div>
      </motion.div>
    </div>
  );
}